import { Tabs } from 'antd';
import { EventSubscription } from 'fbemitter';
import * as React from 'react';
import EditorDispatcher from '../dispatchers/EditorDispatcher';
import SessionDispatcher from '../dispatchers/SessionDispatcher';
import { fetchData, getEditors, login, logout } from '../PoolAPI';
import { fetchAssignmentForEditor } from '../stores/AssignmentStore';
import SessionStore from '../stores/SessionStore';
import '../style/App.css';
import { removeListener } from '../utils';
import { AdminView } from './AdminView';
import { AssignmentsView } from './AssignmentsView';
import { Calendar } from './Calendar';
import { DistributeAssignmentsView } from './DistributeAssignmentsView';
import { LoginView } from './LoginView';

interface AppState {
  loggedIn: boolean;
  initialFetchPending: boolean;

  data?: any; // tslint:disable-line
}

class App extends React.Component<{}, AppState> {
  private sessionListener: EventSubscription | null = null;

  constructor(props: {}) {
    super(props);

    this.state = {
      loggedIn: SessionStore.isLoggedIn(),
      initialFetchPending: true,
    };

    this.initialFetch();
  }

  render(): JSX.Element {
    if (!this.state.loggedIn && !this.state.initialFetchPending) {
      return (
        <LoginView
          onLoggedIn={async () => {
            const r = await login();
            if (r.status !== 200) {
              return;
            }

            await this.initialFetch();
          }}
        />
      );
    }

    if (this.state.initialFetchPending || this.state.data === undefined) {
      return <div />;
    }

    const data = this.state.data;

    let editorView = null;
    if (data.isEditor && data.calendarInfo !== undefined) {
      editorView = (
        <div className="main-content">
          <div className="space-out">
            <h1>
              {data.name} ({data.role})
            </h1>
          </div>
          <h2>
            {data.poolDaysNeeded} Pool Day{data.poolDaysNeeded === 1 ? '' : 's'}{' '}
            in {data.month}
          </h2>

          <p>
            A preliminary assignment of pool days has been generated for you.
            You can modify your bank days and pool sign-up days below.
          </p>
          <p>
            Click <strong>Pick Helpful Days</strong> to choose a new selection
            where the most help is needed. You will be able to modify your
            selection before submitting.
          </p>
          <p>
            You can take at most <strong>{data.allowedOffsiteDays}</strong>{' '}
            offsite day{data.allowedOffsiteDays === 1 ? '' : 's'} per{' '}
            {data.offsiteUnitOfTime}. To mark offsite days in the calendar, you
            should shift-click them on the calendar or email the Managing Editor
            directly.
          </p>
          {data.specialMessageFromEditor !== '' ? (
            <p style={{ whiteSpace: 'pre-line' }}>
              {data.specialMessageFromEditor}
            </p>
          ) : null}

          <br />

          <Calendar
            firstDayOfWeek={data.calendarInfo.firstDayOfWeek}
            daysInMonth={data.calendarInfo.daysInMonth}
            startingWorkDay={data.calendarInfo.startingWorkDay}
            endingWorkDay={data.calendarInfo.endingWorkDay}
            existingWorkers={data.calendarInfo.existingWorkers}
            numDaysNeeded={data.poolDaysNeeded}
            maxBankDays={Math.floor(data.poolDaysNeeded / 2)}
            allowedOffsiteDays={data.allowedOffsiteDays}
            islandsAllowed={data.islandsAllowed}
            offsiteUnitOfTime={data.offsiteUnitOfTime}
            initialBankDays={data.initialBankDays}
            initialDays={data.initialDays}
          />
        </div>
      );
    }

    const signout = (
      <a className="signout" onClick={() => this.signout()}>
        {data.name === undefined ? null : 'Not ' + data.name + '? '}Sign out.
      </a>
    );

    return (
      <Tabs tabBarExtraContent={signout}>
        {data.isAdmin ? (
          <Tabs.TabPane tab="Distribute Assignments" key={1}>
            <div className="main-content">
              <DistributeAssignmentsView />
            </div>
          </Tabs.TabPane>
        ) : null}
        {data.isEditor && data.calendarInfo !== undefined ? (
          <Tabs.TabPane tab="Pick Days" key={2}>
            {editorView}
          </Tabs.TabPane>
        ) : null}
        {data.isEditor || data.isAdmin ? (
          <Tabs.TabPane tab="Assignments" key={3}>
            <div className="main-content">
              <AssignmentsView />
            </div>
          </Tabs.TabPane>
        ) : null}
        {data.isAdmin ? (
          <Tabs.TabPane tab="Admin Panel" key={4}>
            <div className="main-content">
              <AdminView />
            </div>
          </Tabs.TabPane>
        ) : null}
      </Tabs>
    );
  }

  componentDidMount() {
    this.sessionListener = SessionStore.addListener(() => {
      this.setState({
        loggedIn: SessionStore.isLoggedIn(),
      });
    });
  }

  componentWillUnmount() {
    removeListener(this.sessionListener);
  }

  signout() {
    var auth2 = window.gapi.auth2.getAuthInstance();

    Promise.all([auth2.signOut(), logout()]).then(() => {
      SessionDispatcher.handleLogout();
    });
  }

  private async initialFetch() {
    const r = await fetchData();
    if (r.status >= 400) {
      this.setState({
        initialFetchPending: false,
      });
      return;
    }

    const j = await r.json();
    if (j.email !== undefined) {
      SessionDispatcher.handleLogin(j.email);
    }
    this.setState({
      initialFetchPending: false,
      data: j,
    });

    const rEditor = await getEditors();
    if (rEditor.status >= 400) {
      const text = await rEditor.text();
      console.error(text);
      return;
    }

    const jEditor = await rEditor.json();
    EditorDispatcher.handleUpdateEditors(jEditor);

    fetchAssignmentForEditor(j.email);
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.tsx